import { defineStore, storeToRefs } from 'pinia';
import type { OutcomeForm, RtoFee } from './types';
import { PAYMENT_METHODS } from './types';
import { DONATION_ROUTES, INTAKE_ROUTES, INTAKES_WITH_PAYMENT, OUTCOME_ROUTES, OUTCOMES_WITH_CART } from './constants';
import { CART_STEPS } from './types';
import { CART_TYPES } from './enums';
import {
  GetCartActiveDiscounts,
  GetCartSessionService,
  GetCashDrawerOptionsService,
  GetLostAndFoundOutcomeFormService,
  GetOutcomeDetails,
  GetOutcomeFormsService,
  GetRehomeOutcomeFormService,
  GetRtoFeesService,
} from './services';
import { useSessionStore } from '@Stores';
import { INTAKE_TYPES } from '@IntakeModule';

const {
  ADD_PEOPLE,
  DONATION,
  CHECKOUT_METHOD,
  MEDICAL_COSTS,
  MOBILE_CHECKOUT,
  MOBILE_PROCESSING,
  OUTCOME_INFO,
  PAYMENT_METHOD,
  PRODUCTS_INFO,
  PRODUCTS_AND_SERVICES,
  SUMMARY,
} = CART_STEPS;

interface TriggerCompleteOutcomeSignal {
  isMobile: boolean;
  signal: number;
}

// TODO - AF-527 :: Each step should have a proper interface
interface StepData {
  [ADD_PEOPLE]: any;
  [DONATION]: any;
  [CHECKOUT_METHOD]: any;
  [MEDICAL_COSTS]: any;
  [MOBILE_CHECKOUT]: any;
  [MOBILE_PROCESSING]: any;
  [OUTCOME_INFO]: any;
  [PAYMENT_METHOD]: any;
  [PRODUCTS_AND_SERVICES]: any;
  [PRODUCTS_INFO]: any;
  [SUMMARY]: any;
}

interface State {
  activeCartStep: number;
  cartAside: boolean;
  cartAsideButton: boolean;
  cartData: any;
  cardFieldError: string;
  cardHolderName: string;
  cashDrawerOptions: any;
  checkoutPendingData: any; // TODO : AF-530 Probably deprecated
  confirmingCartWithTotalZero: null | boolean;
  discounts: any;
  fetchingCart: boolean; // TODO : AF-528 Still needs implementation
  forms: OutcomeForm[];
  hasRtoFee: boolean;
  hasWaivers: boolean;
  loading: boolean;
  mainLoading: boolean;
  outcomeCheckoutType: any;
  outcomeCompleted: boolean;
  outcomeForm: any; // TODO : AF-530 Probably deprecated
  outcomeInfoActiveStep: number; // TODO : AF-530 Probably deprecated
  peopleForm: any;
  redirectUrl: string;
  rtoFees: RtoFee[];
  selectedRtoFee: RtoFee[];
  stepState: StepData;
  triggerCompleteOutcomeSignal: TriggerCompleteOutcomeSignal | null;
  emptyCartConfirmCallback: (() => void | Promise<void>) | null;
  pendingIntakePersonFromProfile: any;
}

const initialStepState = {
  [ADD_PEOPLE]: {},
  [DONATION]: {},
  [CHECKOUT_METHOD]: {},
  [MEDICAL_COSTS]: {},
  [MOBILE_CHECKOUT]: {},
  [MOBILE_PROCESSING]: {},
  [OUTCOME_INFO]: {},
  [PAYMENT_METHOD]: {},
  [PRODUCTS_AND_SERVICES]: {},
  [PRODUCTS_INFO]: {},
  [SUMMARY]: {},
};

const STATE: State = {
  activeCartStep: 0,
  cartAside: false,
  cartAsideButton: true,
  cartData: {},
  cardFieldError: '',
  cardHolderName: '',
  cashDrawerOptions: [],
  checkoutPendingData: {},
  confirmingCartWithTotalZero: null,
  discounts: [],
  fetchingCart: false, // TODO : AF-528 Still needs implementation
  forms: [],
  hasRtoFee: false,
  hasWaivers: false,
  loading: false,
  mainLoading: true,
  outcomeCheckoutType: null,
  outcomeCompleted: false,
  outcomeForm: {}, // TODO : AF-530 Probably deprecated
  outcomeInfoActiveStep: 0,
  peopleForm: undefined,
  redirectUrl: '',
  rtoFees: [],
  selectedRtoFee: [],
  stepState: initialStepState,
  triggerCompleteOutcomeSignal: null,
  emptyCartConfirmCallback: null,
  pendingIntakePersonFromProfile: null,
};

export default defineStore('cart', {
  state: (): State => ({ ...STATE }),

  getters: {
    // TODO - AF-529 - All getters should be using getActions
    activeDiscount: (state: State) => state.cartData?.discount || 0,
    activeDiscountCode: (state: State) => state.cartData?.discount_code || '',
    animals: (state: State) => state.cartData?.animals || [],
    anonymousDonations: (state: State) => state.cartData?.is_anonymous_donation,
    cartId: (state: State) => state.cartData?.cart_id?.$oid || state.cartData?.cart_id || '',
    cartTotal: (state: State) => Number(state.cartData?.total) || 0,
    citations: (state: State) => state.cartData?.citations || [],
    donations: (state: State) => state.cartData?.donations_details || [],
    isAdoption: (state: State) => state.cartData?.outcome_type === 'adopted',
    isCheckoutPending: (state: State) => state.cartData?.checkout_status === 'pending',
    isCitation: (state: State) => state.cartData?.cart_type === 'citation',
    isDonation: (state: State) => state.cartData?.cart_type === 'donation',
    isEuthanasia: (state: State) => state.cartData?.outcome_type === INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA,
    isIntake: (state: State) => state.cartData?.cart_type === 'intake',
    isIntakeWithNoFees: (state: State) => !state.cartData?.has_fees,
    isProduct: (state: State) => state.cartData?.cart_type === 'product',
    isReturnToOwner: (state: State) => state.cartData?.outcome_type === 'return_to_owner',
    isService: (state: State) => state.cartData?.cart_type === 'service',
    isTransferOut: (state: State) => state.cartData?.outcome_type === 'transfer_out',
    outcomeType: (state: State) => state.cartData?.outcome_type || '',
    people: (state: State) => state.cartData?.people || undefined,
    products: (state: State) => state.cartData?.products || [],
    productsLength: (state: State) => state.cartData?.products?.length,
    services: (state: State) => state.cartData?.services || [],
    servicesLength: (state: State) => state.cartData?.services?.length,
    status: (state: State) => state.cartData?.status || undefined,
    type: (state: State) => state.cartData?.cart_type || undefined,

    hasCart: (state: State) => {
      return (
        OUTCOMES_WITH_CART.includes(state.cartData?.outcome_type) ||
        (state.cartData?.cart_type === 'intake' && INTAKES_WITH_PAYMENT.includes(state.cartData?.outcome_type))
      );
    },

    completeOutcomeRoute(state: State) {
      const cartId = state.cartData?.cart_id?.$oid || state.cartData?.cart_id;
      if (!cartId) return null;

      // prettier-ignore
      switch (state.cartData?.cart_type) {
        case 'intake': return `${INTAKE_ROUTES.completed}${cartId}`;
        case 'donation': return `${DONATION_ROUTES.completed}${cartId}`;
        default: return `${OUTCOME_ROUTES.completed}${cartId}`;
      }
    },

    pendingOutcomeRoute(state: State) {
      const cartId = state.cartData?.cart_id?.$oid || state.cartData?.cart_id;
      if (!cartId) return null;

      // prettier-ignore
      switch (state.cartData?.cart_type) {
        case 'intake': return `${INTAKE_ROUTES.pending}${cartId}`;
        case 'donation': return `${DONATION_ROUTES.pending}${cartId}`;
        default: return `${OUTCOME_ROUTES.pending}${cartId}`;
      }
    },

    cartLength: (state: State) =>
      (state.cartData?.animals ? state.cartData?.animals.length : 0) +
      (state.cartData?.citations ? state.cartData?.citations.length : 0) +
      (state.cartData?.products ? state.cartData?.products.length : 0) +
      (state.cartData?.donations_details ? state.cartData?.donations_details.length : 0) +
      (state.cartData?.services ? state.cartData?.services.length : 0),

    emptyCart: (state: State) =>
      !state.cartData?.is_anonymous_donation &&
      (!state.cartData?.animals || !state.cartData?.animals.length) &&
      (!state.cartData?.citations || !state.cartData?.citations.length) &&
      (!state.cartData?.services || !state.cartData?.services.length) &&
      (!state.cartData?.donations_details || !state.cartData?.donations_details.length) &&
      (!state.cartData?.products || !state.cartData?.products.length),

    outcomeHasWaivers: (state: State) =>
      state.hasWaivers || state.cartData?.animals?.some((animal: any) => animal?.waivers?.length) || false,
  },

  actions: {
    // GETTERS
    cartTypeIsCitation: (cartData: any) => cartData?.cart_type === 'citation',
    cartTypeIsDonation: (cartData: any) => cartData?.cart_type === 'donation',
    cartTypeIsIntake: (cartData: any) => cartData?.cart_type === 'intake',
    cartTypeIsProduct: (cartData: any) => cartData?.cart_type === 'product',
    cartTypeIsService: (cartData: any) => cartData?.cart_type === 'service',
    getAnimals: (cartData: any) => cartData?.animals || [],
    getCitations: (cartData: any) => cartData?.citations || [],
    getDonations: (cartData: any) => cartData.donations_details || [],
    getIntakeHasNoFees: (cartData: any) => !cartData?.has_fees,
    getOutcomeType: (cartData: any) => cartData?.outcome_type || '',
    getProducts: (cartData: any) => cartData?.products || [],
    getServices: (cartData: any) => cartData?.services || [],
    getStatus: (cartData: any) => cartData?.status,
    getType: (cartData: any) => cartData?.cart_type,

    getCompletedOutcomeRoute(cartId: string, cartType?: string) {
      // prettier-ignore
      switch (cartType) {
        case 'intake': return `${INTAKE_ROUTES.completed}${cartId}`;
        case 'donation': return `${DONATION_ROUTES.pending}${cartId}`;
        default: return `${OUTCOME_ROUTES.completed}${cartId}`;
      }
    },

    getHasCart: (cartData: any) => {
      return (
        OUTCOMES_WITH_CART.includes(cartData?.outcome_type) ||
        (cartData?.cart_type === 'intake' && INTAKES_WITH_PAYMENT.includes(cartData?.outcome_type))
      );
    },

    getPendingOutcomeRoute(cartId: string, cartType?: string) {
      // prettier-ignore
      switch (cartType) {
        case 'intake': return `${INTAKE_ROUTES.pending}${cartId}`;
        case 'donation': return `${DONATION_ROUTES.pending}${cartId}`;
        default: return `${OUTCOME_ROUTES.pending}${cartId}`;
      }
    },

    getCartLength(cartData: any) {
      return (
        (cartData?.animals ? cartData?.animals.length : 0) +
        (cartData?.citations ? cartData?.citations.length : 0) +
        (cartData?.products ? cartData?.products.length : 0) +
        (cartData?.donations_details ? cartData.donations_details.length : 0) +
        (cartData?.services ? cartData?.services.length : 0)
      );
    },

    isIntakeInWithNoPayment(cartData: any) {
      return (
        cartData?.cart_type === 'intake' && !Number(cartData?.donation || 0) && !Number(cartData?.total_charge || 0)
      );
    },

    showPaymentSummary(cartData: any): boolean {
      if (!cartData) return false;

      const { CITATION, DONATION, PRODUCT, INTAKE } = CART_TYPES;
      const { cart_type, outcome_type } = cartData;

      if ([CITATION, DONATION, PRODUCT].includes(cart_type)) return true;

      if (cart_type === INTAKE) return !(this.isIntakeInWithNoPayment(cartData) || this.getIntakeHasNoFees(cartData));

      if (!OUTCOMES_WITH_CART.includes(outcome_type)) return false;

      return true;
    },

    isCardReaderLastFourInvalid(digits?: number, paymentMethod?: PAYMENT_METHODS) {
      return paymentMethod === PAYMENT_METHODS.CARD_READER && (!digits || digits.toString().length !== 4);
    },

    setCompleteOutcomeTriggerSignal(isMobile: boolean) {
      this.triggerCompleteOutcomeSignal = {
        isMobile,
        signal: Number(new Date().getTime()),
      };
    },

    // PROVIDERS
    // TODO - AF-528 - Review this logic to hit the API only the necessary amount of time.
    async fetchCompletedOutcome(cartId: string) {
      return GetOutcomeDetails(cartId)
        .then((data: any) => {
          // NOTE : Empty cart
          if (!data?.cart_id?.$oid) return;

          // NOTE : Mobile checkout
          const isCheckoutPending = data.checkout_status === 'pending';
          if (isCheckoutPending) {
            this.checkoutPendingData = data;
            this.redirectUrl = this.getPendingOutcomeRoute(data.cart_id.$oid);
            return;
          }

          // NOTE : Outcome completed
          this.outcomeCompleted = true;
          this.redirectUrl = this.getCompletedOutcomeRoute(data.cart_id.$oid);
          return;
        })
        .finally(() => (this.fetchingCart = false));
    },

    async fetchCashDrawerOptions() {
      const { shelterProfile } = storeToRefs(useSessionStore());
      if (!shelterProfile.value?.cash_drawer) return;

      return await GetCashDrawerOptionsService().then((data: any) => (this.cashDrawerOptions = data));
    },

    async fetchActiveCart(cartIdParam?: string) {
      this.fetchingCart = true;

      return GetCartSessionService(cartIdParam)
        .then((data: any) => {
          const cartId = data?.cart_id?.$oid || data?.cart_id;
          if (!cartId && cartIdParam) this.fetchCompletedOutcome(cartIdParam);

          this.cartData = data;
        })
        .finally(() => (this.fetchingCart = false));
    },

    async fetchActiveDiscounts() {
      return GetCartActiveDiscounts().then((data: any) => (this.discounts = data));
    },

    async fetchOutcomeForms(displayFormType?: string) {
      this.loading = true;

      let formService: () => Promise<OutcomeForm[]>;

      //NOTE : Do not use pretter-ignore here as formService() needs to be called
      switch (displayFormType) {
        case 'outcome':
          formService = GetOutcomeFormsService;
          break;
        case 'lost_found':
          formService = GetLostAndFoundOutcomeFormService;
          break;
        case 'rehome':
          formService = GetRehomeOutcomeFormService;
          break;
        default:
          return Promise.reject(new Error('Invalid form type'));
      }

      return formService()
        .then((data: OutcomeForm[]) => {
          this.forms = data;
          this.getRtoFees();
        })
        .finally(() => (this.loading = false));
    },

    async getRtoFees() {
      await GetRtoFeesService().then(({ rto_fees }: any) => (this.hasRtoFee = !!rto_fees.length));
    },

    async refreshData() {
      this.loading = true;
      return this.fetchActiveCart(this.$state.cartData.cart_id.$oid).finally(() => (this.loading = false));
    },

    // RESETS
    resetCart() {
      this.$state = {
        ...STATE,
        cartData: {},
        discounts: this.discounts,
        forms: this.forms,
        hasRtoFee: this.hasRtoFee,
        emptyCartConfirmCallback: null,
        pendingIntakePersonFromProfile: null,
      };
    },

    outcomeCompletedCartReset() {
      // NOTE : This method is here in case we need to do specific resets for completed outcomes
      this.resetCart();

      return true;
    },
  },
});
